/**
 * Structured Data (JSON-LD) for SEO
 * Used across the website for rich snippets in search results
 */

const baseUrl = 'https://www.hillpointglobal.com';

// Organization Schema
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'HillPoint Global',
  alternateName: 'HillPoint',
  url: baseUrl,
  logo: `${baseUrl}/hillpoint-logo.png`,
  description: 'Leading provider of acoustic engineering solutions in the Middle East and India, specializing in acoustic panels, soundproofing, and architectural acoustics.',
  foundingDate: '2008',
  founders: [
    {
      '@type': 'Person',
      name: 'Kimi Alexander',
      jobTitle: 'Chairman'
    },
    {
      '@type': 'Person',
      name: 'Manoj Paulose',
      jobTitle: 'Director'
    }
  ],
  address: {
    '@type': 'PostalAddress',
    streetAddress: '#104, Block 11 Sayer-II, Barwa Commercial Avenue',
    postOfficeBoxNumber: '200091',
    addressLocality: 'Doha',
    addressCountry: 'QA',
    addressRegion: 'Doha'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+974-4032-8777',
    contactType: 'Customer Service',
    areaServed: ['QA', 'AE', 'IN', 'SA'],
    availableLanguage: ['English', 'Arabic', 'Hindi']
  },
  sameAs: [
    'https://www.linkedin.com/company/hillpoint',
    'https://www.hillpointglobal.com'
  ],
  areaServed: {
    '@type': 'GeoCircle',
    geoMidpoint: {
      '@type': 'GeoCoordinates',
      latitude: '25.2854',
      longitude: '51.5310'
    },
    geoRadius: '5000'
  }
};

// Local Business Schemas (Multiple Offices)
export const officeSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${baseUrl}#qatar-office`,
    name: 'HillPoint Global - Qatar Headquarters',
    image: `${baseUrl}/hillpoint-logo.png`,
    url: baseUrl,
    telephone: '+974-4032-8777',
    email: 'sales@hillpointglobal.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '#104, Block 11 Sayer-II, Barwa Commercial Avenue',
      postOfficeBoxNumber: '200091',
      addressLocality: 'Doha',
      addressCountry: 'QA'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '25.2854',
      longitude: '51.5310'
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Sunday'],
        opens: '08:00',
        closes: '17:00'
      }
    ]
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${baseUrl}#dubai-office`,
    name: 'HillPoint Global - Dubai Office',
    image: `${baseUrl}/hillpoint-logo.png`,
    url: baseUrl,
    telephone: '+971-04-3214942',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'AL Manara Tower, Office No.1211, Business Bay, Sheikh Zayed Road',
      postOfficeBoxNumber: '120077',
      addressLocality: 'Dubai',
      addressCountry: 'AE'
    }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${baseUrl}#bangalore-office`,
    name: 'HillPoint Global - Bangalore Office',
    image: `${baseUrl}/hillpoint-logo.png`,
    url: baseUrl,
    telephone: '+91-9790967946',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'No 3rd, 2nd Floor, Dasarahalli Main Road, Opposite Karagadamba Temple, Dasarahalli, Hebbal Post',
      addressLocality: 'Bangalore',
      postalCode: '560024',
      addressRegion: 'Karnataka',
      addressCountry: 'IN'
    }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${baseUrl}#jeddah-office`,
    name: 'HillPoint Global - Jeddah Office',
    image: `${baseUrl}/hillpoint-logo.png`,
    url: baseUrl,
    telephone: '+966-50-039-8926',
    email: 'contactsaudi@hillpointglobal.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Building 1366, Arafat AL, Hamra District',
      addressLocality: 'Jeddah',
      addressCountry: 'SA'
    }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${baseUrl}#coimbatore-factory`,
    name: 'HillPoint Global - Manufacturing Unit',
    image: `${baseUrl}/hillpoint-logo.png`,
    url: baseUrl,
    email: 'contactindia@hillpointglobal.com',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Coimbatore',
      addressRegion: 'Tamil Nadu',
      addressCountry: 'IN'
    }
  }
];

// Service Schema
export const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Acoustic Engineering Solutions',
  provider: {
    '@type': 'Organization',
    name: 'HillPoint Global',
    url: baseUrl
  },
  areaServed: [
    {
      '@type': 'Country',
      name: 'Qatar'
    },
    {
      '@type': 'Country',
      name: 'United Arab Emirates'
    },
    {
      '@type': 'Country',
      name: 'India'
    },
    {
      '@type': 'Country',
      name: 'Saudi Arabia'
    }
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Acoustic Solutions',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Acoustic Panels',
          description: 'Premium wooden and fabric acoustic panels for sound absorption'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Soundproofing Solutions',
          description: 'Complete soundproofing for corporate and residential spaces'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Acoustic Treatment',
          description: 'Professional acoustic treatment for auditoriums, studios, and offices'
        }
      }
    ]
  }
};

// Breadcrumb Schema Generator
export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${baseUrl}${item.url}`
  }))
});

// Article Schema for News/Media
export const generateArticleSchema = (article) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${baseUrl}/media/${article.slug}`,
  headline: article.title,
  description: article.excerpt,
  image: article.images[0],
  datePublished: article.date,
  author: {
    '@type': 'Organization',
    name: 'HillPoint Global'
  },
  publisher: {
    '@type': 'Organization',
    name: 'HillPoint Global',
    logo: {
      '@type': 'ImageObject',
      url: `${baseUrl}/hillpoint-logo.png`
    }
  }
});

// FAQ Schema for common questions
export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What acoustic solutions does HillPoint Global offer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'HillPoint Global offers a comprehensive range of acoustic solutions including wooden acoustic panels, PET panels, fabric panels, acoustic doors, movable partitions, and specialized products like acoustic louvers and metal ceiling panels.'
      }
    },
    {
      '@type': 'Question',
      name: 'Where is HillPoint Global located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'HillPoint Global has offices in Qatar (Headquarters), Dubai (UAE), Bangalore (India), Jeddah (Saudi Arabia), and a manufacturing facility in Coimbatore, Tamil Nadu (India).'
      }
    },
    {
      '@type': 'Question',
      name: 'What industries does HillPoint Global serve?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'HillPoint Global serves Corporate, Education, Auditorium, GYM & Cafe, Hospitality, Airport & Stadium, and Cinema & Studio sectors. The public project portfolio is organised into these categories, with representative installation photography and labels for each.'
      }
    }
  ]
};

